import { ReactNode, useContext, useMemo } from 'react'

import { AuthContext } from '~/lib/util/fe/dataContext/authenticationContext'
import {
  ProtectPageOption,
  Role,
  satisfiesProtectPageOption
} from '~/types/roles'

type AuthorizedAccessProps = {
  admin?: boolean
  children: ReactNode
  contentCreator?: boolean
  creator?: boolean
  billingRead?: boolean
  billingWrite?: boolean
  requireCampaign?: boolean
  requireUser?: boolean
  checkPermission?: boolean
  canSendBroadcasts?: boolean
  // Preferred over the individual role props: pass the same ProtectPageOption
  // the page hands protectPage, so the redirect rule and the render rule are one
  // declaration and cannot disagree.
  option?: ProtectPageOption
}

const AuthorizedAccess = ({
  admin = false,
  children,
  contentCreator = false,
  creator = false,
  billingRead = false,
  billingWrite = false,
  requireCampaign = true,
  requireUser = true,
  checkPermission = true,
  canSendBroadcasts = false,
  option = undefined
}: AuthorizedAccessProps) => {
  const {
    authorizedValues: { campaign: currentCampaign, user: currentUser }
  } = useContext(AuthContext)

  const isRequiredUserMissing = () => {
    return requireUser && !currentUser
  }
  const isRequiredCampaignMissing = () => {
    return requireCampaign && !currentCampaign
  }
  const isRequiredBillingReadMissing = () => {
    return (
      billingRead &&
      !(
        currentUser?.isCreator ||
        ['read', 'write'].includes(currentUser?.billingPermissions)
      ) &&
      !currentUser?.isAdmin
    )
  }
  const isRequiredBillingWriteMissing = () => {
    return (
      billingWrite &&
      !(
        currentUser?.isCreator ||
        ['write'].includes(currentUser?.billingPermissions)
      ) &&
      !currentUser?.isAdmin
    )
  }

  // The role props are an OR: render when the user satisfies ANY enabled one
  // (admins satisfy all of them). `option` supersedes them when given.
  const isRoleRequirementMissing = () => {
    if (option) return !satisfiesProtectPageOption(option, currentUser)
    if (!admin && !contentCreator && !canSendBroadcasts) return false
    return (
      !currentUser?.isAdmin &&
      !(contentCreator && currentUser?.role === Role.CONTENT_CREATOR) &&
      !(canSendBroadcasts && currentUser?.can_send_broadcasts)
    )
  }

  const isRequiredCreatorMissing = () => {
    return creator && !currentUser?.isCreator
  }

  const isRequiredCheckPermissionMissing = () => {
    return !checkPermission && !currentUser.isAdmin
  }

  const checkShouldHideChildren = () => {
    return (
      isRequiredUserMissing() ||
      isRequiredCampaignMissing() ||
      isRequiredBillingReadMissing() ||
      isRequiredBillingWriteMissing() ||
      isRoleRequirementMissing() ||
      isRequiredCreatorMissing() ||
      isRequiredCheckPermissionMissing()
    )
  }

  const showChildren = useMemo(
    () => !checkShouldHideChildren(),
    [
      currentCampaign,
      currentUser,
      requireCampaign,
      requireUser,
      admin,
      contentCreator,
      creator,
      billingRead,
      billingWrite,
      canSendBroadcasts,
      checkPermission,
      option
    ]
  )

  return <>{showChildren && <>{children}</>}</>
}

export default AuthorizedAccess
